import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRYDE_LOGO = "https://customer-assets.emergentagent.com/job_bd-crm-stryde/artifacts/qdu0hd2a_stryde-fin-01.jpg";

export default function Login() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email: email.toLowerCase().trim() });
      localStorage.setItem("stryde_user", JSON.stringify(response.data));
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized email address. Please use your Stryde email.");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#151B19] flex-col justify-center items-center p-12">
        <div className="max-w-md text-center animate-fade-in">
          <img 
            src={STRYDE_LOGO} 
            alt="Stryde Research" 
            className="w-64 mx-auto mb-8"
            data-testid="login-logo"
          />
          <h1 className="text-3xl font-bold text-white mb-4 font-['Outfit'] tracking-tight">
            Business Development CRM
          </h1>
          <p className="text-[#A1A1AA] text-lg leading-relaxed">
            Track pharmaceutical sponsor leads with strategic tier prioritization. 
            Manage email follow-up sequences and monitor feasibility opportunities.
          </p>
          <div className="mt-12 grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#D97757]">29</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Gold</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#A1A1AA]">20</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Silver</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#D97757]/70">194</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Bronze</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">1,294</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9F8F6]">
        <Card className="w-full max-w-md shadow-lg border-0 animate-fade-in" data-testid="login-card">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="lg:hidden mb-6">
              <img 
                src={STRYDE_LOGO} 
                alt="Stryde Research" 
                className="w-48 mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold font-['Outfit'] tracking-tight">Welcome Back</CardTitle>
            <CardDescription className="text-base">
              Sign in with your Stryde email to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@stryderesearch.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 text-base"
                    data-testid="login-email-input"
                    autoFocus
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold bg-[#2D5A4C] hover:bg-[#23473C] transition-all duration-200"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Authorized users: Chintan, Vaibhavi, Ed, Nona
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
